import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose
} from 'src/@/components/ui/dialog';
import { Button } from 'src/@/components/ui/button';
import { ScrollArea, ScrollBar } from 'src/@/components/ui/scroll-area';
import { Textarea } from 'src/@/components/ui/textarea';
import v2_actionAgentsPredictions from 'src/actions/v2/ai/v2_actionAgentsPredictions';
import { Loader2, Trash2, Send } from 'lucide-react';
import Iconify from 'src/components/Iconify';
import v2_actionPredictionAnalyzeChat from 'src/actions/v2/ai/v2_actionPredictionAnalyzeChat';

export default function ModalAiChat({
  agentId,
  aiAgent,
  open,
  dataSendAi,
  onOpenChange,
  triggerLabel,
  buttonDisabled,
  suggestions = [
    "faça um breve resumo dos atendimentos",
    "houve atendimentos ruins?",
    "quantos atendimentos ?"
  ]
}) {
  const [userInput, setUserInput] = useState('');
  const [conversation, setConversation] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const scrollAreaRef = useRef(null);


  useEffect(() => {
    if (!open) return;
    const savedConversation = localStorage.getItem(`chat_ai_analize_history_${agentId}`);
    if (savedConversation) {
      setConversation(JSON.parse(savedConversation));
    }
  }, [open]);

  useEffect(() => {
    if (conversation.length > 0) {
      localStorage.setItem(`chat_ai_analize_history_${agentId}`, JSON.stringify(conversation));
    }
  }, [conversation]);

  useEffect(() => {
    if (open && scrollAreaRef.current) {
      const scrollArea = scrollAreaRef.current;
      scrollArea.scrollTop = scrollArea.scrollHeight;
    }
  }, [conversation, open]);

  const simulateTyping = async (content) => {
    setIsTyping(true);
    await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 1000));
    setIsTyping(false);
    return content;
  };

  const handleClearChat = () => {
    setConversation([]);
    localStorage.setItem(`chat_ai_analize_history_${agentId}`, JSON.stringify([]));
  };

  const handleSubmit = async () => {
    if (!userInput.trim() || isLoading) return;
    try {
      setIsLoading(true);
      const newMessage = { role: 'user', content: userInput };
      const updatedConversation = [...conversation, newMessage];
      setConversation(updatedConversation);
      setUserInput('');
      const data = { scope: { history: updatedConversation, ...dataSendAi } };
      const response = await v2_actionPredictionAnalyzeChat(agentId, data);
      const assistantMessage = response.data.data;
      const processedMessage = await simulateTyping(assistantMessage);
      const aiResponse = { role: 'assistant', content: processedMessage };
      const finalConversation = [...updatedConversation, aiResponse];
      setConversation(finalConversation);
      localStorage.setItem(`chat_ai_analize_history_${agentId}`, JSON.stringify(finalConversation));
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button
          disabled={buttonDisabled}
          className="flex items-center gap-2 px-4 py-2.5 bg-[#7C3AED] hover:bg-[#6d28d9] text-white text-sm font-medium rounded-xl transition-all shadow-sm shadow-[#7C3AED]/20 active:transform active:scale-95"
        >
          <Iconify icon="mingcute:ai-fill" width={20} />
          {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl p-0 gap-0 overflow-hidden rounded-2xl shadow-2xl border-0 bg-white [&>button.absolute]:hidden">
        <DialogHeader className="p-6 pb-4 border-b border-gray-100 bg-white">
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-3">
              <DialogTitle className="text-lg font-bold text-gray-900 flex items-center gap-3 tracking-tight">
                <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center">
                  <Iconify icon="mingcute:ai-fill" className="text-[#7C3AED]" width={20} />
                </div>
                <div className="flex flex-col">
                  <span>Análise com IA</span>
                  <span className="text-xs font-normal text-gray-500">
                    Converse com a IA sobre os atendimentos filtrados
                  </span>
                </div>
              </DialogTitle>
              <DialogClose
                className="w-8 h-8 inline-flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-900 hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-[#7C3AED]/30"
                aria-label="Fechar"
              >
                <Iconify icon="eva:close-outline" width={18} />
              </DialogClose>
            </div>

            <div className="flex items-start gap-2.5 px-3 py-2 bg-violet-50/60 rounded-lg border border-violet-100/80">
              <Iconify icon="material-symbols:info-rounded" className="text-[#7C3AED] mt-0.5 flex-shrink-0" width={16} />
              <p className="text-xs text-violet-900/80 font-medium leading-relaxed">
                A IA analisará apenas os dados filtrados atualmente na tela.
              </p>
            </div>
          </div>
        </DialogHeader>
        <div className="flex flex-col h-[60vh]">
          {/* Mensagens do chat */}
          <div className="flex-1 overflow-hidden">
            <ScrollArea className="h-full" viewportRef={scrollAreaRef}>
              <div className="flex flex-col gap-4 p-4">
                {conversation.map((msg, index) => (
                  <div
                    key={index}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`relative px-5 py-4 rounded-2xl max-w-[85%] text-sm leading-relaxed shadow-sm ${msg.role === 'user'
                          ? 'bg-[#7C3AED] text-white rounded-tr-sm'
                          : 'bg-[#F9FAFB] border border-gray-100 text-gray-800 rounded-tl-sm'
                        }`}
                    >
                      <p className="whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  </div>
                ))}
                {isTyping && (
                  <div className="flex justify-start">
                    <div className="bg-[#F9FAFB] border border-gray-100 px-5 py-4 rounded-2xl rounded-tl-sm shadow-sm">
                      <div className="flex gap-1.5 items-center">
                        <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" />
                        <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:-.15s]" />
                        <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:-.3s]" />
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <ScrollBar />
            </ScrollArea>
          </div>
          {/* Bottom bar */}
          <div className="border-t border-gray-100 bg-gradient-to-b from-gray-50/60 to-white">
            {/* Sugestões */}
            {suggestions && suggestions.length > 0 && conversation.length === 0 && (
              <div className="px-6 pt-4 pb-2">
                <div className="flex items-center gap-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2.5">
                  <Iconify icon="eva:flash-outline" width={12} />
                  Sugestões rápidas
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {suggestions.map((s, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setUserInput(s)}
                      className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-full hover:border-[#7C3AED] hover:text-[#7C3AED] hover:bg-violet-50/40 transition-all"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Input */}
            <div className="px-6 py-4">
              <div className="relative flex items-end gap-2 bg-white border border-gray-200 rounded-2xl px-2.5 py-2 shadow-sm focus-within:border-[#7C3AED] focus-within:ring-2 focus-within:ring-[#7C3AED]/15 transition-all">
                <Textarea
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  placeholder="Pergunte algo sobre os atendimentos..."
                  rows={1}
                  className="flex-1 min-h-[36px] max-h-32 py-1.5 px-2 resize-none bg-transparent border-0 focus-visible:ring-0 focus-visible:ring-offset-0 text-gray-900 text-sm placeholder:text-gray-400 shadow-none"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSubmit();
                    }
                  }}
                />

                {conversation.length > 0 && (
                  <button
                    type="button"
                    onClick={handleClearChat}
                    title="Limpar conversa"
                    className="h-9 w-9 inline-flex items-center justify-center rounded-xl text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors flex-shrink-0"
                  >
                    <Trash2 size={16} />
                  </button>
                )}

                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isLoading || !userInput.trim()}
                  className={`h-9 w-9 inline-flex items-center justify-center rounded-xl transition-all flex-shrink-0 ${
                    !userInput.trim() || isLoading
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-[#7C3AED] hover:bg-[#6d28d9] text-white shadow-sm shadow-[#7C3AED]/30 hover:shadow-md hover:shadow-[#7C3AED]/30 active:scale-95'
                  }`}
                >
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </button>
              </div>

              <div className="flex items-center justify-between mt-2 px-1">
                <span className="text-[10px] text-gray-400">
                  Pressione <kbd className="px-1 py-0.5 rounded bg-gray-100 text-gray-500 text-[10px] font-mono">Enter</kbd> para enviar · <kbd className="px-1 py-0.5 rounded bg-gray-100 text-gray-500 text-[10px] font-mono">Shift+Enter</kbd> para nova linha
                </span>
                {conversation.length > 0 && (
                  <span className="text-[10px] text-gray-400">
                    {conversation.length} {conversation.length === 1 ? 'mensagem' : 'mensagens'}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
